import { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase";

const AuthContext = createContext(null);
const googleProvider = new GoogleAuthProvider();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = still resolving session

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const namePart = firebaseUser.displayName || firebaseUser.email.split("@")[0];
        const name = namePart.charAt(0).toUpperCase() + namePart.slice(1);
        setUser({ email: firebaseUser.email, name, uid: firebaseUser.uid });
      } else {
        setUser(null);
      }
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password.trim());
      return { ok: true };
    } catch (err) {
      const msg =
        ["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"].includes(err.code)
          ? "Invalid email or password."
          : err.code === "auth/too-many-requests"
          ? "Too many attempts. Please try again later."
          : "Sign in failed. Please try again.";
      return { ok: false, error: msg };
    }
  };

  const loginWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      return { ok: true };
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") return { ok: false, error: "" };
      return { ok: false, error: "Google sign-in failed. Please try again." };
    }
  };

  const logout = () => signOut(auth);

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const firebaseUser = auth.currentUser;
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePassword(firebaseUser, newPassword);
      return { ok: true };
    } catch (err) {
      const msg =
        ["auth/wrong-password", "auth/invalid-credential"].includes(err.code)
          ? "Current password is incorrect."
          : "Password change failed. Please try again.";
      return { ok: false, error: msg };
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return { ok: true };
    } catch (err) {
      return { ok: false, error: "Could not send reset email. Check the address and try again." };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, logout, changePassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
